import React, { useState } from 'react';
import type { FAQComponent } from '../../../types/builder';
import { ChevronDown } from 'lucide-react';

interface FAQRendererProps {
  component: FAQComponent;
}

export const FAQRenderer: React.FC<FAQRendererProps> = ({ component }) => {
  const { title, items, backgroundColor, textColor, padding, style } = component.settings;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqStyle = style || 'default';

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Default Style
  if (faqStyle === 'default') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-3xl mx-auto">
          {title && <h2 className="text-2xl font-bold text-center mb-8">{title}</h2>}
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={item.id} className="bg-white/5 rounded-lg overflow-hidden border border-white/10">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-semibold pr-4">{item.question}</span>
                  <ChevronDown
                    className={`flex-shrink-0 w-5 h-5 transition-transform ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4 text-sm opacity-80 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Modern Style
  if (faqStyle === 'modern') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-3xl mx-auto">
          {title && <h2 className="text-3xl font-bold text-center mb-10 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">{title}</h2>}
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className={`rounded-2xl overflow-hidden transition-all ${
                openIndex === index 
                  ? 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-2 border-purple-500/30 shadow-lg' 
                  : 'bg-white/5 border border-white/10'
              }`}>
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-bold text-lg pr-4">{item.question}</span>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    openIndex === index ? 'bg-purple-600' : 'bg-gray-600'
                  } transition-colors`}>
                    <ChevronDown
                      className={`w-5 h-5 text-white transition-transform ${
                        openIndex === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5 text-base opacity-90 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Minimal Style
  if (faqStyle === 'minimal') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-2xl mx-auto">
          {title && <h2 className="text-xl font-semibold text-center mb-6">{title}</h2>}
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={item.id} className="border-b border-gray-200/30">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-100/5 transition-colors"
                >
                  <span className="font-medium text-sm pr-4">{item.question}</span>
                  <ChevronDown
                    className={`flex-shrink-0 w-4 h-4 transition-transform ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-4 pb-3 text-sm opacity-70 leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Card Style
  if (faqStyle === 'card') {
    return (
      <div style={{ backgroundColor, color: textColor, padding: `${padding}px` }}>
        <div className="max-w-4xl mx-auto">
          {title && <h2 className="text-2xl font-bold text-center mb-8">{title}</h2>}
          <div className="grid gap-4">
            {items.map((item, index) => (
              <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  style={{ color: '#1f2937' }}
                >
                  <span className="font-semibold pr-4">{item.question}</span>
                  <ChevronDown
                    className={`flex-shrink-0 w-5 h-5 transition-transform ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

